import React, { useEffect, useState } from "react";
import { makeStyles } from "@mui/styles";
import {
  TextField,
  Button,
  InputAdornment,
  Select,
  MenuItem,
} from "@mui/material";
import Colors from "../../theme/colors.json";
import {
  COMMISSION_INFO_SECTIONS,
  COMMISSION_INFO_VALIDATIONS,
  COMMISSION_STATUS_LIST,
  CONFIRMATION_CONTENT,
  CONFIRMATION_TITLE,
  REWARD_STATUS_LIST,
  USER_GROUPS,
  handleValueRoundOff,
} from "../../constants/constants";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import { useSelector } from "react-redux";

const useStyles = makeStyles((theme) => ({
  commissionInfoTable: {
    width: "100%",
    borderCollapse: "collapse",
    border: `1px solid ${Colors.BORDER}`,
    marginBottom: "20px",
    "& th, & td": {
      border: `1px solid ${Colors.BORDER}`,
      padding: "8px",
      textAlign: "left",
    },
    background: Colors.WHITE_BACKGROUND,
    borderRadius: "0.6rem",
  },
  commissionInfoTableHeading: {
    color: theme.palette.secondary.main,
    fontSize: theme.fontSizes.MEDIUM,
    fontWeight: 600,
  },
  validationContainer: {
    display: "flex",
    justifyContent: "flex-end",
  },
  tableTextFieldStyles: {
    fontSize: theme.fontSizes.SMALL,
    width: "9.6vw",
  },
  validationAction: {
    pointerEvents: "none",
  },
  commonBtnStyles: {
    background: Colors.WHITE_BACKGROUND,
    textTransform: "capitalize",
  },
  commissionInfoTableHeadingCell: {
    fontSize: theme.fontSizes.SMALL,
    fontWeight: 600,
  },
  commissionInfoTableCell: {
    fontSize: theme.fontSizes.SMALL,
  },
  flexColumn: {
    display: "flex",
    flexDirection: "column",
  },
  commonSelect: {
    width: "100%",
    fontSize: theme.fontSizes.SMALL,
    height: "1.875rem !important",
  },
  validationCell: {
    display: "flex",
    flexDirection: "column",
    gap: "0.7rem",
  },
}));

const CommissionInfo = ({ commissionDetails, setCommissionDetails }) => {
  const classes = useStyles();
  const { userGroup } = useSelector((state: any) => ({
    userGroup: state.userGroup,
  }));
  const premiumSections = [
    {
      label: "Net Premium / OD (other than Motor TP, Terrorism & ERF)",
      section: COMMISSION_INFO_SECTIONS.NET_PREMIUM,
    },
    {
      label: "Motor TP Premium / Terrorism / Premium for ERF in case PL Act",
      section: COMMISSION_INFO_SECTIONS.MOTORTPPREMIUM,
    },
  ];

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedValidation, setSelectedValidation] = useState("");

  const handleYesCommissionAmount = () => {
    setConfirmDialogOpen(false);
    setCommissionDetails((prevData) => ({
      ...prevData,
      commissionAmountValidation: true,
    }));
  };

  const handleYesRewardAmount = () => {
    setConfirmDialogOpen(false);
    setCommissionDetails((prevData) => ({
      ...prevData,
      rewardAmountValidation: true,
    }));
  };

  const handleValidationButtonClicks = (value) => {
    setSelectedValidation(value);
    setConfirmDialogOpen(true);
  };

  const handleNo = () => {
    setConfirmDialogOpen(false);
  };

  const handleInputChange = async (e, section) => {
    const { name, value } = e.target;
    // const newValue = !isNaN(value) ? parseFloat(value) : value;
    const newValue = value;

    await setCommissionDetails((prevData) => {
      const sectionData = prevData[section];
      const netPremium = sectionData.netPremium;
      const Commission = sectionData.commission;
      const Reward = sectionData.reward;

      const commissionAmount: any =
        (parseFloat(newValue) *
          (name === "netPremium" ? Commission : netPremium)) /
        100;

      const totalNetPremium =
        (name === "netPremium" ? parseFloat(value) : parseFloat(netPremium)) +
        (section === COMMISSION_INFO_SECTIONS.NET_PREMIUM
          ? parseFloat(prevData?.motorTpPremium?.netPremium)
          : prevData?.netPremium?.netPremium);
      const totalCommissionAmount =
        parseFloat(commissionAmount) +
        (section === COMMISSION_INFO_SECTIONS.NET_PREMIUM
          ? parseFloat(prevData?.motorTpPremium?.commissionAmount)
          : parseFloat(prevData?.netPremium?.commissionAmount));

      const updatedData = {
        ...prevData,
        [section]: {
          ...sectionData,
          [name]: newValue,
          commissionAmount: parseFloat(commissionAmount),
          reward: parseFloat(Reward),
          rewardAmount: (parseFloat(Reward) * parseFloat(netPremium)) / 100,
        },
        totalPremium: {
          totalNetPremium: handleValueRoundOff(totalNetPremium),
          totalCommissionAmount: handleValueRoundOff(totalCommissionAmount),
        },
      };

      return updatedData;
    });
  };

  const handleInputChangeReward = async (e, section) => {
    const { name, value } = e.target;
    // const newValue = !isNaN(value) ? parseFloat(value) : value;
    const newValue = value;

    await setCommissionDetails((prevData) => {
      const sectionData = prevData[section];
      const netPremium = sectionData.netPremium;
      const Reward = sectionData.reward;

      const rewardAmount: any =
        (parseFloat(newValue) * parseFloat(netPremium)) / 100;

      const totalRewardAmount =
        parseFloat(rewardAmount) +
        (section === COMMISSION_INFO_SECTIONS.NET_PREMIUM
          ? parseFloat(prevData?.motorTpPremium?.rewardAmount)
          : parseFloat(prevData?.netPremium?.rewardAmount));

      const updatedData = {
        ...prevData,
        [section]: {
          ...sectionData,
          [name]: newValue,
          rewardAmount: parseFloat(rewardAmount),
        },
        totalPremium: {
          ...prevData?.totalPremium,
          totalRewardAmount: handleValueRoundOff(totalRewardAmount),
        },
      };

      return updatedData;
    });
  };

  const handleInputChangeSelection = (e, section) => {
    const { name, value } = e.target;
    if (name === "netPremium" || name === "commission") {
      handleInputChange(e, section);
    } else if (name === "reward") {
      handleInputChangeReward(e, section);
    }
  };

  const handleFieldRendering = (section, field) => {
    return (
      <td key={field}>
        <TextField
          type={"number"}
          name={field}
          size="small"
          disabled={
            field === "commissionAmount" ||
            field === "rewardAmount" ||
            field === "netPremium"
              ? true
              : false
          }
          value={commissionDetails[section?.section][field]}
          onChange={(e) => handleInputChangeSelection(e, section?.section)}
          InputProps={{
            ...((field === "commission" || field === "reward") && {
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }),
            ...(field !== "commission" &&
              field !== "reward" && {
                startAdornment: (
                  <InputAdornment position="start">₹</InputAdornment>
                ),
              }),
            className: `${classes.tableTextFieldStyles} ${
              field === "commission"
                ? commissionDetails?.commissionAmountValidation === true
                  ? classes.validationAction
                  : ""
                : commissionDetails?.rewardAmountValidation === true
                ? classes.validationAction
                : ""
            }`,
          }}
        />
      </td>
    );
  };

  const handleValidationYesClick = (field) => {
    switch (field) {
      case "rewardAmount":
        return handleYesRewardAmount;
      default:
        return handleYesCommissionAmount;
    }
  };

  const handleStatusChange = async (e) => {
    const { name, value } = e.target;
    await setCommissionDetails((prevData) => ({
      ...prevData,
      [name]: value, // Update the specific property
    }));
  };

  useEffect(() => {
    setCommissionDetails((prevData) => ({
      ...prevData,
      totalPremium: {
        ...prevData.totalPremium,
        totalNetPremium: handleValueRoundOff(
          parseFloat(
            prevData[COMMISSION_INFO_SECTIONS.NET_PREMIUM].netPremium
          ) +
            parseFloat(
              prevData[COMMISSION_INFO_SECTIONS.MOTORTPPREMIUM].netPremium
            )
        ),
        totalCommissionAmount: handleValueRoundOff(
          prevData[COMMISSION_INFO_SECTIONS.NET_PREMIUM].commissionAmount +
            prevData[COMMISSION_INFO_SECTIONS.MOTORTPPREMIUM].commissionAmount
        ),
        totalRewardAmount: handleValueRoundOff(
          prevData[COMMISSION_INFO_SECTIONS.NET_PREMIUM].rewardAmount +
            prevData[COMMISSION_INFO_SECTIONS.MOTORTPPREMIUM].rewardAmount
        ),
      },
    }));
  }, [commissionDetails.netPremium, commissionDetails.motorTpPremium]);

  return (
    <div>
      <ConfirmationDialog
        open={confirmDialogOpen}
        setOpen={setConfirmDialogOpen}
        title={CONFIRMATION_TITLE}
        content={CONFIRMATION_CONTENT}
        handleYes={handleValidationYesClick(selectedValidation)}
        handleNo={handleNo}
      />
      <table className={classes.commissionInfoTable}>
        <thead>
          <tr>
            <th colSpan={8} className={classes.commissionInfoTableHeading}>
              Commission Info
            </th>
          </tr>
          <tr>
            <th></th>
            <th className={classes.commissionInfoTableHeadingCell}>
              Net Premium
            </th>
            <th className={classes.commissionInfoTableHeadingCell}>
              Commision %
            </th>
            <th className={classes.commissionInfoTableHeadingCell}>
              Commission Amount
            </th>
            <th className={classes.commissionInfoTableHeadingCell}>Reward %</th>
            <th className={classes.commissionInfoTableHeadingCell}>
              Reward Amount
            </th>
          </tr>
        </thead>
        <tbody>
          {premiumSections.map((section) => (
            <tr
              key={section?.label}
              className={classes.commissionInfoTableCell}
            >
              <td>{section?.label}</td>
              {Object.keys(commissionDetails[section?.section]).map((field) =>
                handleFieldRendering(section, field)
              )}
            </tr>
          ))}

          <tr>
            <td className={classes.commissionInfoTableCell}>TOTAL PREMIUM</td>

            <td>
              <TextField
                type="number"
                name="netPremium"
                size="small"
                disabled
                value={commissionDetails?.totalPremium?.totalNetPremium}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">₹</InputAdornment>
                  ),
                  className: classes.tableTextFieldStyles,
                }}
              />
            </td>
            <td>
              {/* <TextField
                type="number"
                name="gst"
                size="small"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">%</InputAdornment>
                  ),
                }}
              /> */}
            </td>
            <td>
              <TextField
                type="number"
                name="totalCommissionAmount"
                size="small"
                disabled
                value={commissionDetails?.totalPremium?.totalCommissionAmount}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">₹</InputAdornment>
                  ),
                  className: classes.tableTextFieldStyles,
                }}
              />
            </td>

            <td />
            <td>
              <TextField
                type="number"
                name="totalRewardAmount"
                size="small"
                disabled
                value={commissionDetails?.totalPremium?.totalRewardAmount}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">₹</InputAdornment>
                  ),
                  className: classes.tableTextFieldStyles,
                }}
              />
            </td>
          </tr>
          <tr>
            <td></td>
            <td></td>
            <td></td>
            <td className={classes.validationCell}>
              <Button
                variant="outlined"
                size="small"
                color="primary"
                className={
                  commissionDetails?.commissionAmountValidation === true
                    ? classes.validationAction
                    : classes.commonBtnStyles
                }
                onClick={() =>
                  handleValidationButtonClicks(
                    COMMISSION_INFO_VALIDATIONS.Commission_Amount
                  )
                }
                disabled={
                  commissionDetails?.commissionAmountValidation === true
                    ? true
                    : false
                }
              >
                Validation
              </Button>

              {userGroup === USER_GROUPS.ADMIN ? (
                <Button
                  className={classes.commonBtnStyles}
                  onClick={() =>
                    setCommissionDetails((prevData) => ({
                      ...prevData,
                      commissionAmountValidation: false,
                    }))
                  }
                  variant="outlined"
                  size="small"
                  disabled={
                    commissionDetails?.commissionAmountValidation === true
                      ? false
                      : true
                  }
                >
                  Revalidation
                </Button>
              ) : null}
            </td>
            <td></td>
            <td className={classes.validationCell}>
              <Button
                variant="outlined"
                size="small"
                color="primary"
                className={
                  commissionDetails?.rewardAmountValidation === true
                    ? classes.validationAction
                    : classes.commonBtnStyles
                }
                onClick={() =>
                  handleValidationButtonClicks(
                    COMMISSION_INFO_VALIDATIONS.Reward_Amount
                  )
                }
                disabled={
                  commissionDetails?.rewardAmountValidation === true
                    ? true
                    : false
                }
              >
                Validation
              </Button>
              {userGroup === USER_GROUPS.ADMIN ? (
                <Button
                  className={classes.commonBtnStyles}
                  onClick={() =>
                    setCommissionDetails((prevData) => ({
                      ...prevData,
                      rewardAmountValidation: false,
                    }))
                  }
                  variant="outlined"
                  size="small"
                  disabled={
                    commissionDetails?.rewardAmountValidation === true
                      ? false
                      : true
                  }
                >
                  Revalidation
                </Button>
              ) : null}
            </td>
          </tr>
          <tr>
            <td />
            <td />
            <td colSpan={2}>
              <div className={classes.flexColumn}>
                <label className={classes.commissionInfoTableHeadingCell}>
                  Commission Status
                </label>
                <Select
                  size="small"
                  className={classes.commonSelect}
                  name="commissionStatus"
                  onChange={handleStatusChange}
                  value={commissionDetails?.commissionStatus}
                >
                  {COMMISSION_STATUS_LIST?.map((item, index) => {
                    return (
                      <MenuItem key={index} value={item?.value}>
                        {item?.title}
                      </MenuItem>
                    );
                  })}
                </Select>
              </div>
            </td>
            <td colSpan={2}>
              <div className={classes.flexColumn}>
                <label className={classes.commissionInfoTableHeadingCell}>
                  Reward Status
                </label>
                <Select
                  size="small"
                  className={classes.commonSelect}
                  name="rewardStatus"
                  onChange={handleStatusChange}
                  value={commissionDetails?.rewardStatus}
                >
                  {REWARD_STATUS_LIST?.map((item, index) => {
                    return (
                      <MenuItem key={index} value={item?.value}>
                        {item?.title}
                      </MenuItem>
                    );
                  })}
                </Select>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default CommissionInfo;
